/**
 * Orchestration over the transport and the reducer.
 *
 * The machine owns connections, watchdogs, backoff, and retries. It owns no
 * stream logic: every belief about the stream is a `StreamState` produced by
 * the reducer, and every phase change goes through a reducer transition. The
 * split matters because the reducer is where the invariants live, and the
 * conformance runner drives this exact machine over a Node transport to prove
 * them against the real server.
 *
 * Reconnect policy, from the specification:
 *
 *   delay = random(0, min(30000, 500 * 2 ** attempt))
 *
 * Five attempts, counted per outage: a connection that decodes at least one
 * frame resets the count, because the policy limits consecutive failures, not
 * lifetime reconnects. Every attempt sends the current cursor. A 429 with a
 * usable Retry-After waits exactly that long and does not count.
 *
 * Time, randomness, and timers are injected. The machine never names a
 * platform global, which is what keeps it compilable with neither the DOM
 * library nor the Node types.
 */

import {
  createFrameDecoder,
  toNormalized,
  type DecodedFrame,
  type MatildaChatStreamEvent,
} from '@harness/protocol';

import {
  clearRateLimit,
  createInitialState,
  isTerminalPhase,
  markCancelled,
  markConnecting,
  markRateLimited,
  markReconnecting,
  markReconnectsExhausted,
  markRetry,
  markWatchdogFired,
  reduceFrames,
  type StreamState,
} from './reducer.ts';
import {
  createCancelSource,
  TransportHttpError,
  type CancelSource,
  type StreamRequest,
  type StreamTransport,
} from './transport.ts';
import { createWatchdogs, type TimerHost, type Watchdogs } from './watchdog.ts';

export const MAX_RECONNECT_ATTEMPTS = 5;
export const RECONNECT_BASE_DELAY_MS = 500;
export const RECONNECT_MAX_DELAY_MS = 30_000;

export interface StreamMachineOptions {
  transport: StreamTransport;
  /** POST target that starts a stream. */
  streamUrl: string;
  /** GET target that resumes one. The machine appends the streamId query. */
  resumeUrl: string;
  /** JSON body for the starting POST. */
  body?: unknown;
  timers: TimerHost;
  /** A monotonic clock. The browser passes performance.now, tests pass their own. */
  now: () => number;
  /** Uniform in [0, 1). Feeds the full jitter backoff. */
  random: () => number;
  maxReconnectAttempts?: number;
  byteTimeoutMs?: number;
  contentTimeoutMs?: number;
  /** Called after every state change with the new immutable state. */
  onChange?: (state: StreamState) => void;
  /**
   * Called for every applied layer B event, and with a synthesised
   * `response.cursor` after each applied frame that carried an id. The cursor
   * member has no wire event; this is where it is born.
   */
  onEvent?: (event: MatildaChatStreamEvent) => void;
}

export interface StreamMachine {
  readonly state: StreamState;
  /** Open the stream. Resolves with the terminal state. */
  start(): Promise<StreamState>;
  /** Abort. Partial text is retained and the phase becomes cancelled. */
  cancel(): void;
  /**
   * Continue after error, stalled, or cancelled, keeping text and cursor.
   * Resolves with the terminal state of the resumed run.
   */
  retry(): Promise<StreamState>;
}

interface ConnectionOutcome {
  terminal: boolean;
}

export function createStreamMachine(options: StreamMachineOptions): StreamMachine {
  const maxAttempts = options.maxReconnectAttempts ?? MAX_RECONNECT_ATTEMPTS;

  let state = createInitialState();
  let runPromise: Promise<StreamState> | null = null;
  let master: CancelSource = createCancelSource();
  let connection: CancelSource | null = null;
  /**
   * Whether the most recent connection decoded at least one frame. Held here
   * rather than on the connection's return value because a connection can
   * make progress and then throw, and progress made before a failure still
   * resets the consecutive attempt count.
   */
  let connectionProgressed = false;

  const publish = (): void => {
    options.onChange?.(state);
  };

  const setState = (next: StreamState): void => {
    state = next;
    publish();
  };

  /** A timer wait the user can cancel out of. */
  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      const unsubscribe = master.signal.onAbort(() => {
        options.timers.clearTimeout(handle);
        resolve();
      });
      const handle = options.timers.setTimeout(() => {
        unsubscribe();
        resolve();
      }, ms);
    });

  const emitApplied = (
    before: StreamState,
    after: StreamState,
    frames: readonly DecodedFrame[],
  ): void => {
    const onEvent = options.onEvent;
    if (onEvent === undefined) return;

    // New frame entries correspond, in order, to the subset of frames the
    // reducer applied. Matching on id and type re-derives the event object
    // without asking the reducer to return more than state.
    let cursor = 0;
    for (const entry of after.timeline.slice(before.timeline.length)) {
      if (entry.kind !== 'frame') continue;
      while (cursor < frames.length) {
        const frame = frames[cursor];
        cursor += 1;
        if (frame === undefined || frame.kind !== 'event') continue;
        const event = toNormalized(frame);
        if (event === null || event.type !== entry.type || frame.id !== entry.id) continue;
        onEvent(event);
        if (entry.id !== null) {
          onEvent({ type: 'response.cursor', lastEventId: String(entry.id) });
        }
        break;
      }
    }
  };

  const handleFrames = (frames: readonly DecodedFrame[], watchdogs: Watchdogs): void => {
    const before = state;
    state = reduceFrames(before, frames, options.now());
    emitApplied(before, state, frames);
    publish();
    watchdogs.phaseChanged(state.phase);
  };

  const buildRequest = (signal: StreamRequest['signal']): StreamRequest => {
    if (state.streamId === null) {
      return { url: options.streamUrl, body: options.body, signal };
    }
    return {
      url: `${options.resumeUrl}?streamId=${encodeURIComponent(state.streamId)}`,
      lastEventId: String(state.lastEventId),
      signal,
    };
  };

  const connectOnce = async (): Promise<ConnectionOutcome> => {
    const source = createCancelSource();
    connection = source;
    connectionProgressed = false;
    const detachMaster = master.signal.onAbort(() => {
      source.cancel();
    });

    const decoder = createFrameDecoder();

    const watchdogs: Watchdogs = createWatchdogs({
      timers: options.timers,
      ...(options.byteTimeoutMs === undefined ? {} : { byteTimeoutMs: options.byteTimeoutMs }),
      ...(options.contentTimeoutMs === undefined
        ? {}
        : { contentTimeoutMs: options.contentTimeoutMs }),
      onByteTimeout: () => {
        // The connection itself went quiet. Note it and force a reconnect.
        setState(markWatchdogFired(state, options.now(), 'byte'));
        source.cancel();
      },
      onContentTimeout: () => {
        // Bytes are flowing and tokens are not. The connection stays open:
        // reconnecting a healthy connection accomplishes nothing.
        setState(markWatchdogFired(state, options.now(), 'content'));
        watchdogs.phaseChanged(state.phase);
      },
    });

    try {
      const iterable = options.transport.open(buildRequest(source.signal));
      watchdogs.start();
      watchdogs.phaseChanged(state.phase);

      for await (const chunk of iterable) {
        watchdogs.bytesSeen();
        const frames = decoder.push(chunk);
        if (frames.length > 0) {
          connectionProgressed = true;
          // A data frame resets the content watchdog; a keepalive does not.
          // That difference is the entire reason the two timers exist.
          if (frames.some((frame) => frame.kind !== 'keepalive')) watchdogs.contentSeen();
          handleFrames(frames, watchdogs);
        }
        if (isTerminalPhase(state.phase)) {
          source.cancel();
          return { terminal: true };
        }
        if (source.signal.aborted) break;
      }

      const rest = decoder.flush();
      if (rest.length > 0) {
        connectionProgressed = true;
        handleFrames(rest, watchdogs);
      }
      return { terminal: isTerminalPhase(state.phase) };
    } finally {
      watchdogs.stop();
      detachMaster();
      connection = null;
    }
  };

  const runLoop = async (): Promise<StreamState> => {
    // Consecutive failures since the last connection that decoded a frame.
    let attempt = 0;

    for (;;) {
      if (isTerminalPhase(state.phase)) return state;

      try {
        const outcome = await connectOnce();
        if (outcome.terminal || isTerminalPhase(state.phase)) return state;
      } catch (error) {
        if (isTerminalPhase(state.phase)) return state;
        if (
          error instanceof TransportHttpError &&
          error.status === 429 &&
          error.retryAfterSeconds !== undefined
        ) {
          // Honoured exactly, and not counted against the attempt limit.
          setState(markRateLimited(state, error.retryAfterSeconds));
          await sleep(error.retryAfterSeconds * 1000);
          if (isTerminalPhase(state.phase)) return state;
          setState(clearRateLimit(state));
          continue;
        }
        // Any other failure falls through to the backoff below.
      }

      if (isTerminalPhase(state.phase)) return state;
      // The limit is on consecutive failures, not lifetime reconnects: any
      // connection that decoded a frame starts the count over, whether it
      // ended cleanly or threw.
      if (connectionProgressed) attempt = 0;
      if (attempt >= maxAttempts) {
        setState(markReconnectsExhausted(state, attempt));
        return state;
      }

      const delay =
        options.random() * Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * 2 ** attempt);
      attempt += 1;
      setState(markReconnecting(state, options.now(), attempt));
      await sleep(delay);
    }
  };

  const launch = (): Promise<StreamState> => {
    const running = runLoop().finally(() => {
      runPromise = null;
    });
    runPromise = running;
    return running;
  };

  return {
    get state(): StreamState {
      return state;
    },

    start(): Promise<StreamState> {
      if (state.phase !== 'idle') {
        throw new Error(
          `start() was called in phase "${state.phase}". A machine starts once; use retry() to continue.`,
        );
      }
      setState(markConnecting(state));
      return launch();
    },

    cancel(): void {
      if (isTerminalPhase(state.phase)) return;
      setState(markCancelled(state, options.now()));
      master.cancel();
    },

    retry(): Promise<StreamState> {
      if (runPromise !== null && state.phase === 'stalled') {
        // The connection is open and idle. Drop it; the loop reconnects with
        // the cursor it already holds.
        setState(markRetry(state, options.now()));
        connection?.cancel();
        return runPromise;
      }
      if (!isTerminalPhase(state.phase) && state.phase !== 'stalled') {
        throw new Error(
          `retry() was called in phase "${state.phase}". It continues a stream that stopped.`,
        );
      }
      setState(markRetry(state, options.now()));
      master = createCancelSource();
      return launch();
    },
  };
}
